import { Link } from "react-router-dom";
import { Lightbulb, CheckCircle2, Leaf, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const SECTIONS = [
  {
    num: "01",
    title: "Inspect Your Clones Upon Arrival",
    blocks: [
      { t: "p", v: "When your cannabis clones arrive, the first step is to carefully inspect them. Look for healthy green leaves and a well-developed root system." },
      { t: "tip", v: "If you notice any wilting, yellowing, or root damage, act quickly to revive your clone by placing it in a gentle, warm, and moist environment with adequate light." },
      { t: "why", v: "Early detection of issues helps prevent further stress to the plant and gives you the best start for an excellent finish." },
    ],
  },
  {
    num: "02",
    title: "Acclimatize Your Clones",
    blocks: [
      { t: "p", v: "After the journey to your home, clones can be a bit shocked. They need to acclimatize to their new environment before being transplanted or exposed to full light intensity." },
      { t: "subhead", v: "Steps For Acclimatization" },
      { t: "list", v: [
        "Place your clones in a warm, slightly shaded area with indirect light for the first 24–48 hours.",
        "Avoid immediately exposing them to high-intensity grow lights or direct sunlight, as this can stress them.",
      ] },
      { t: "tip", v: "If possible, gradually increase light exposure after the first couple of days to help them adjust." },
      { t: "why", v: "A gradual acclimatization period helps prevent transplant shock, which can slow down growth and weaken the plant." },
    ],
  },
  {
    num: "03",
    title: "Prepare Your Growing Medium",
    blocks: [
      { t: "p", v: "Before transplanting your cannabis clone, make sure your soil is properly prepared. Healthy soil should be well-aerated and nutrient-rich." },
      { t: "subhead", v: "For Soil" },
      { t: "list", v: [
        "Use a high-quality, light soil mix with good drainage.",
        "Ensure the pH is between 6.0 and 6.5 for optimal nutrient absorption.",
      ] },
    ],
  },
  {
    num: "04",
    title: "Transplanting Your Clones",
    blocks: [
      { t: "p", v: "Once your clones have acclimatized, they are ready to be transplanted into their new home." },
      { t: "subhead", v: "How To Transplant" },
      { t: "list", v: [
        "Gently remove the clone from its current container, being careful not to damage the delicate roots.",
        "Place the clone into a hole in the growing medium that is deep enough to support the root structure.",
        "Lightly pack the medium around the stem to provide stability without compacting the soil too tightly.",
      ] },
      { t: "tip", v: "We recommend a full saturation watering after transplanting. This helps the soil coagulate and encourages the roots to spread out into the nutrient-rich, moist soil." },
      { t: "why", v: "Proper transplanting allows the roots to establish themselves quickly, promoting vigorous growth and reducing the risk of transplant shock." },
    ],
  },
  {
    num: "05",
    title: "Lighting And Temperature Control",
    blocks: [
      { t: "p", v: "After transplanting, cannabis clones need the right amount of light and a stable environment to grow." },
      { t: "subhead", v: "Lighting" },
      { t: "list", v: [
        "For the vegetative stage, cannabis clones require 18–24 hours of light per day. Fluorescent or LED grow lights are ideal for providing the right spectrum without generating excessive heat.",
      ] },
      { t: "subhead", v: "Temperature And Humidity" },
      { t: "list", v: [
        "Maintain temperatures between 20°C and 26°C (68°F–79°F).",
        "Keep humidity levels between 60% and 70% during the first few days, then gradually lower to around 50% as the plant matures.",
      ] },
      { t: "tip", v: "Use a hygrometer and thermometer to regularly monitor the growing environment." },
      { t: "why", v: "A stable light cycle and proper environmental conditions are critical for photosynthesis and root development, which are the foundations of strong, healthy plants." },
    ],
  },
  {
    num: "06",
    title: "Watering And Nutrient Care",
    blocks: [
      { t: "p", v: "Cannabis clones require careful watering and nutrient management, as over- or under-watering can harm the delicate root system." },
      { t: "subhead", v: "Watering Tips" },
      { t: "list", v: [
        "Water your clone only when the top inch of soil feels dry. Overwatering can drown the roots and lead to root rot.",
        "Use room temperature, pH-balanced water (6.0–6.5 for soil, 5.5–6.0 for hydroponics).",
      ] },
      { t: "subhead", v: "Nutrient Management" },
      { t: "list", v: [
        "During the early stages, use a diluted nutrient solution (around 25% strength) and gradually increase as the clone develops.",
        "A nitrogen-rich fertilizer is ideal for the vegetative stage, promoting healthy leaf growth.",
      ] },
      { t: "tip", v: "Pay attention to signs of nutrient deficiencies or imbalances, such as yellowing leaves or slow growth, and adjust your feeding schedule accordingly." },
      { t: "why", v: "Proper watering and feeding provide the plant with the moisture and nutrients it needs without overwhelming its developing root system, supporting vigorous, healthy growth." },
    ],
  },
  {
    num: "07",
    title: "Monitor Growth And Adjust Care",
    blocks: [
      { t: "p", v: "As your clone establishes itself, it's important to keep a close eye on its growth and make adjustments as needed. Pruning, training, and adjusting the growing environment will help you shape the plant and optimize its potential." },
      { t: "tip", v: "Regularly check for pests, adjust the light height as the plant grows (if growing indoors), and prune away any dead or yellowing leaves to focus the plant's energy on healthy growth." },
      { t: "why", v: "Monitoring growth helps you catch potential issues early and allows you to create an optimal growing environment for a successful harvest." },
    ],
  },
];

function Block({ block }) {
  if (block.t === "p") return <p className="text-slate-400 text-sm leading-relaxed">{block.v}</p>;
  if (block.t === "subhead")
    return <p className="label-caps text-slate-200 text-xs mt-5 mb-2">{block.v}</p>;
  if (block.t === "list")
    return (
      <ul className="space-y-2 mt-1">
        {block.v.map((item, i) => (
          <li key={i} className="flex gap-3 text-slate-400 text-sm leading-relaxed">
            <Leaf size={15} className="text-emerald-500 shrink-0 mt-0.5" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  if (block.t === "tip")
    return (
      <div className="flex gap-3 bg-emerald-500/10 border border-emerald-500/25 px-4 py-3 mt-4">
        <Lightbulb size={16} className="text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-emerald-100/90 text-sm leading-relaxed"><span className="font-semibold text-emerald-400">Tip: </span>{block.v}</p>
      </div>
    );
  if (block.t === "why")
    return (
      <div className="flex gap-3 border-l-2 border-white/15 pl-4 mt-4">
        <CheckCircle2 size={16} className="text-slate-400 shrink-0 mt-0.5" />
        <p className="text-slate-500 text-sm leading-relaxed"><span className="font-semibold text-slate-300">Why this is important: </span>{block.v}</p>
      </div>
    );
  return null;
}

export default function CloneCare() {
  return (
    <div className="bg-[#050505]">
      <Seo
        title="Clone Care Guide — How to Care for Cannabis Clones | Dark Growers Genetics"
        description="A step-by-step guide to caring for your cannabis clones on arrival: inspection, acclimatization, transplanting, lighting, watering, nutrients and ongoing care."
        canonical="https://darkgrowers.com/clone-care"
      />

      {/* Hero */}
      <section className="border-b border-white/10 bg-gradient-to-b from-emerald-950/20 to-black">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-24">
          <p className="label-caps text-emerald-500 mb-3 flex items-center gap-2">
            <Leaf size={14} /> Grower's Guide
          </p>
          <h1 className="font-display text-5xl md:text-6xl text-slate-50 leading-[0.95]">
            Clone <span className="italic text-emerald-400">Care</span> Guide
          </h1>
          <p className="text-slate-400 text-sm mt-6 leading-relaxed max-w-2xl">
            Everything you need to give your Dark Growers clones the best possible start — from the
            moment they arrive to a thriving, vigorous plant. Follow these seven steps for a strong
            foundation and an excellent finish.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-16 space-y-16">
        {SECTIONS.map((s) => (
          <article key={s.num} data-testid={`clone-care-step-${s.num}`}>
            <div className="flex items-baseline gap-4 mb-4">
              <span className="font-display text-4xl text-emerald-500/40">{s.num}</span>
              <h2 className="font-display text-2xl md:text-3xl text-slate-100">{s.title}</h2>
            </div>
            <div className="space-y-1 pl-0 md:pl-14">
              {s.blocks.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </article>
        ))}

        {/* Final thoughts */}
        <article className="border-t border-white/10 pt-12">
          <h2 className="font-display text-3xl md:text-4xl text-slate-100 mb-5">
            Caring For Your Clones: <span className="italic text-emerald-400">Final Thoughts</span>
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed">
            By following these steps, you'll give your cannabis clones the best possible start.
            Whether you are a seasoned grower or a relative beginner, taking the time to acclimatize,
            transplant, and care for your clones properly will ensure they develop into strong,
            healthy plants. At Dark Growers, we provide top-quality clones to set you on the path to
            success, and with the right care, your plants won't just thrive — they'll flourish into
            the kind of cannabis plants you've been dreaming of.
          </p>
        </article>

        {/* CTA */}
        <div className="border border-emerald-500/25 bg-emerald-500/5 p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="font-display text-2xl text-slate-100">Ready to start your grow?</p>
            <p className="text-slate-400 text-sm mt-2">Shop lab-tested clones with free next-day shipping.</p>
          </div>
          <Link
            to="/shop/clones"
            data-testid="clone-care-shop-btn"
            className="group inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors font-medium px-8 py-3.5 text-sm tracking-wide whitespace-nowrap"
          >
            Shop Clones
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
